import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import {
  FaFacebookF,
  FaYoutube,
  FaEnvelope,
  FaMapMarkerAlt,
  FaPhoneAlt,
} from "react-icons/fa";

import { HiOutlineMenuAlt3 } from "react-icons/hi";
import { IoClose } from "react-icons/io5";

import logo from "./tradeschoollogo.jpg";

const navLinks = [
  {
    text: "Home",
    path: "/",
  },
  {
    text: "Founder's Message",
    path: "/principalMessagePage",
  },
  {
    text: "Campus",
    path: "/campusPage",
  },
  {
    text: "Scan Attendance",
    path: "/teacherAttendanceScanner",
  },
  {
    text: "Login",
    path: "/login",
    button: true,
  },
];

const Navbar = () => {
  const location = useLocation();

  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const mobileMenuRef = useRef(null);
  const mobileToggleRef = useRef(null);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (menuOpen) {
      document.body.classList.add("navbar-menu-open");
    } else {
      document.body.classList.remove("navbar-menu-open");
    }

    return () => {
      document.body.classList.remove("navbar-menu-open");
    };
  }, [menuOpen]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && menuOpen) {
        setMenuOpen(false);
        mobileToggleRef.current?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [menuOpen]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!menuOpen) return;

      const clickedInsideMenu =
        mobileMenuRef.current?.contains(event.target);

      const clickedToggle =
        mobileToggleRef.current?.contains(event.target);

      if (!clickedInsideMenu && !clickedToggle) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [menuOpen]);

  const isActive = (path) => {
    if (path === "/") {
      return location.pathname === "/";
    }

    return location.pathname === path;
  };

  const toggleMenu = () => {
    setMenuOpen((previous) => !previous);
  };

  const closeMenu = () => {
    setMenuOpen(false);
  };

  return (
    <>
      <style>{`
        /* =========================================================
           PROFESSIONAL RESPONSIVE NAVBAR
           WHITE + GREEN + BLACK + YELLOW + RED ACCENT
        ========================================================= */

        :root {
          --sic-green: #087f23;
          --sic-green-dark: #056219;
          --sic-green-deep: #034d13;
          --sic-green-soft: rgba(8, 127, 35, 0.055);

          --sic-yellow: #f4c400;
          --sic-yellow-light: #ffda32;
          --sic-yellow-dark: #d8a900;

          --sic-red: #d90429;
          --sic-red-dark: #b80020;

          --sic-black: #090909;
          --sic-text: #151515;

          --sic-gray: #667085;
          --sic-gray-light: #f5f7f6;

          --sic-white: #ffffff;
          --sic-border: #e8e8e8;

          --sic-shadow:
            0 6px 28px rgba(0, 0, 0, 0.09);

          --sic-transition:
            all 0.25s ease;
        }

        /* =========================================================
           RESET
        ========================================================= */

        .site-header,
        .site-header *,
        .mobile-menu,
        .mobile-menu * {
          box-sizing: border-box;
        }

        body.navbar-menu-open {
          overflow: hidden;
        }

        /* =========================================================
           MAIN HEADER
        ========================================================= */

        .site-header {
          position: fixed;

          top: 0;
          left: 0;
          right: 0;

          width: 100%;

          z-index: 5000;

          font-family:
            "Montserrat",
            "Poppins",
            "Segoe UI",
            Arial,
            sans-serif;

          background: transparent;
        }

        /* =========================================================
           TOP BLACK INFORMATION BAR
        ========================================================= */

        .top-navbar {
          width: 100%;

          min-height: 40px;

          background:
            linear-gradient(
              90deg,
              #050505 0%,
              #0a0a0a 65%,
              #071d0b 100%
            );

          color: var(--sic-white);

          border-bottom:
            1px solid
            rgba(244, 196, 0, 0.30);
        }

        .top-navbar-inner {
          width:
            min(1400px, calc(100% - 40px));

          min-height: 40px;

          margin: 0 auto;

          display: flex;
          align-items: center;
          justify-content: space-between;

          gap: 20px;
        }

        .top-navbar-left,
        .top-navbar-right {
          display: flex;
          align-items: center;
        }

        .top-navbar-left {
          min-width: 0;
        }

        .top-item {
          display: flex;
          align-items: center;

          gap: 8px;

          color:
            rgba(255, 255, 255, 0.82);

          font-size: 11px;
          font-weight: 500;

          line-height: 1.3;
        }

        .top-item svg {
          flex-shrink: 0;

          color:
            var(--sic-yellow);

          font-size: 12px;
        }

        .location-item span {
          overflow: hidden;

          white-space: nowrap;

          text-overflow: ellipsis;
        }

        .top-navbar-right {
          flex-shrink: 0;

          gap: 10px;
        }

        .follow-text {
          color:
            rgba(255, 255, 255, 0.58);

          font-size: 10px;
          font-weight: 700;

          text-transform: uppercase;

          letter-spacing: 0.8px;
        }

        .top-socials {
          display: flex;
          align-items: center;

          gap: 6px;
        }

        .top-socials a {
          width: 26px;
          height: 26px;

          display: flex;
          align-items: center;
          justify-content: center;

          color:
            var(--sic-white);

          background:
            rgba(255, 255, 255, 0.08);

          border-radius: 50%;

          text-decoration: none;

          transition:
            transform 0.25s ease,
            color 0.25s ease,
            background 0.25s ease;
        }

        .top-socials a:hover {
          color:
            var(--sic-black);

          background:
            var(--sic-yellow);

          transform:
            translateY(-2px);
        }

        /* =========================================================
           MAIN WHITE NAVBAR
        ========================================================= */

        .navbar-main {
          width: 100%;

          height: 88px;

          background:
            rgba(255, 255, 255, 0.98);

          border-bottom:
            1px solid
            var(--sic-border);

          transition:
            height 0.25s ease,
            box-shadow 0.25s ease;
        }

        .site-header-scrolled .navbar-main {
          height: 76px;

          box-shadow:
            var(--sic-shadow);
        }

        .navbar-main-inner {
          width:
            min(1400px, calc(100% - 40px));

          height: 100%;

          margin: 0 auto;

          display: flex;
          align-items: center;

          gap: 30px;
        }

        /* =========================================================
           LOGO / BRAND
        ========================================================= */

        .logo-section {
          min-width: 250px;

          display: flex;
          align-items: center;

          gap: 12px;

          color: inherit;

          text-decoration: none;

          transition:
            transform 0.25s ease;
        }

        .logo-section:hover {
          transform:
            translateY(-1px);
        }

        .logo-wrapper {
          width: 61px;
          height: 61px;

          flex-shrink: 0;

          display: flex;
          align-items: center;
          justify-content: center;

          background:
            var(--sic-white);

          border:
            3px solid
            var(--sic-green);

          border-radius: 50%;

          overflow: hidden;

          box-shadow:
            0 3px 12px
            rgba(0, 0, 0, 0.10);
        }

        .navbar-logo {
          width: 100%;
          height: 100%;

          display: block;

          object-fit: contain;
        }

        .school-name {
          min-width: 0;

          display: flex;
          flex-direction: column;
        }

        /* =========================================================
           IGNITE S BRAND
        ========================================================= */

        .school-name h1 {
          margin: 0;

          color:
            var(--sic-green-deep);

          font-family:
            "Montserrat",
            "Poppins",
            "Segoe UI",
            Arial,
            sans-serif;

          font-size: 19px;

          font-weight: 800;

          line-height: 1.1;

          letter-spacing: -0.35px;

          white-space: nowrap;
        }

        .school-name h1 .ignite-s {
          color:
            var(--sic-red);

          font-family:
            "Montserrat",
            "Poppins",
            "Segoe UI",
            Arial,
            sans-serif;

          font-size: 1.08em;

          font-weight: 900;

          letter-spacing: -0.5px;

          display: inline-block;

          position: relative;

          text-shadow:
            0 1px 0 rgba(0, 0, 0, 0.04);

          transition:
            color 0.25s ease,
            transform 0.25s ease;
        }

        .logo-section:hover
          .school-name h1
          .ignite-s {
          color:
            var(--sic-red-dark);

          transform:
            translateY(-1px);
        }

        .school-name p {
          margin: 5px 0 0;

          color:
            var(--sic-gray);

          font-size: 9px;
          font-weight: 700;

          line-height: 1;

          text-transform: uppercase;

          letter-spacing: 2px;
        }

        /* =========================================================
           DESKTOP NAVIGATION
        ========================================================= */

        .desktop-navigation {
          height: 100%;

          margin-left: auto;

          display: flex;
          align-items: center;
        }

        .nav-links {
          height: 100%;

          margin: 0;
          padding: 0;

          display: flex;
          align-items: center;

          gap: 3px;

          list-style: none;
        }

        .nav-links li {
          height: 100%;

          position: relative;

          display: flex;
          align-items: center;
        }

        .nav-links li > a {
          position: relative;

          height: 44px;

          display: flex;
          align-items: center;
          justify-content: center;

          padding: 0 15px;

          color:
            var(--sic-text);

          border-radius: 7px;

          font-size: 13px;
          font-weight: 600;

          text-decoration: none;

          white-space: nowrap;

          overflow: hidden;

          transition:
            color 0.25s ease,
            transform 0.25s ease;
        }

        /* =========================================================
           PREMIUM HOVER BACKGROUND
        ========================================================= */

        .nav-links li:not(:has(.login-button))
          > a::before {

          content: "";

          position: absolute;

          left: 50%;
          top: 50%;

          width: 0;
          height: 0;

          border-radius: 8px;

          background:
            var(--sic-green-soft);

          transform:
            translate(-50%, -50%);

          transition:
            width 0.28s ease,
            height 0.28s ease;

          z-index: 0;
        }

        .nav-links li:not(:has(.login-button))
          > a:hover::before {

          width:
            calc(100% - 2px);

          height:
            calc(100% - 4px);
        }

        /* =========================================================
           CENTER-OUT UNDERLINE
        ========================================================= */

        .nav-links li:not(:has(.login-button))
          > a::after {

          content: "";

          position: absolute;

          left: 50%;
          bottom: 3px;

          width: 0;
          height: 3px;

          background:
            linear-gradient(
              90deg,
              var(--sic-green),
              var(--sic-yellow),
              var(--sic-green)
            );

          border-radius: 20px;

          transform:
            translateX(-50%);

          opacity: 0;

          transition:
            width 0.28s ease,
            opacity 0.2s ease;
        }

        .nav-links li:not(:has(.login-button))
          > a:hover::after {

          width: 55%;

          opacity: 1;
        }

        /* =========================================================
           NAVIGATION HOVER TEXT
        ========================================================= */

        .nav-links li:not(:has(.login-button))
          > a:hover {

          color:
            var(--sic-green);

          transform:
            translateY(-1px);
        }

        /* =========================================================
           ACTIVE LINK
        ========================================================= */

        .nav-links li.active:not(:has(.login-button))
          > a {

          color:
            var(--sic-green);

          font-weight: 700;
        }

        .nav-links li.active:not(:has(.login-button))
          > a::after {

          width: 55%;

          opacity: 1;
        }

        /* =========================================================
           LOGIN BUTTON
        ========================================================= */

        .nav-links .login-button {
          position: relative;

          min-width: 108px;

          height: 42px;

          margin-left: 8px;

          display: flex;
          align-items: center;
          justify-content: center;

          overflow: hidden;

          color:
            var(--sic-black) !important;

          background:
            linear-gradient(
              135deg,
              var(--sic-yellow-light),
              var(--sic-yellow)
            );

          border:
            1px solid
            var(--sic-yellow);

          border-radius: 7px;

          font-weight: 800 !important;

          box-shadow:
            0 4px 12px
            rgba(244, 196, 0, 0.22);

          transition:
            transform 0.25s ease,
            box-shadow 0.25s ease,
            background 0.25s ease;
        }

        .nav-links .login-button::before {
          content: "";

          position: absolute;

          top: 0;
          left: -120%;

          width: 80%;
          height: 100%;

          background:
            linear-gradient(
              90deg,
              transparent,
              rgba(255, 255, 255, 0.58),
              transparent
            );

          transform:
            skewX(-20deg);

          transition:
            left 0.55s ease;
        }

        .nav-links .login-button:hover {
          color:
            var(--sic-black) !important;

          background:
            linear-gradient(
              135deg,
              #ffe36a,
              var(--sic-yellow-light)
            );

          transform:
            translateY(-2px);

          box-shadow:
            0 8px 20px
            rgba(8, 127, 35, 0.18);
        }

        .nav-links .login-button:hover::before {
          left: 140%;
        }

        /* =========================================================
           MOBILE MENU BUTTON
        ========================================================= */

        .mobile-toggle {
          display: none;

          width: 45px;
          height: 45px;

          margin-left: auto;

          align-items: center;
          justify-content: center;

          color:
            var(--sic-green);

          background:
            var(--sic-white);

          border:
            1px solid
            var(--sic-border);

          border-radius: 7px;

          font-size: 27px;

          cursor: pointer;

          transition:
            color 0.25s ease,
            background 0.25s ease,
            border-color 0.25s ease;
        }

        .mobile-toggle:hover,
        .mobile-toggle.toggle-active {
          color:
            var(--sic-black);

          background:
            var(--sic-yellow);

          border-color:
            var(--sic-yellow);
        }

        /* =========================================================
           MOBILE BACKDROP
        ========================================================= */

        .mobile-backdrop {
          position: fixed;

          inset: 0;

          z-index: 4998;

          background:
            rgba(0, 0, 0, 0.55);

          opacity: 0;

          visibility: hidden;

          backdrop-filter:
            blur(3px);

          -webkit-backdrop-filter:
            blur(3px);

          transition:
            opacity 0.3s ease,
            visibility 0.3s ease;
        }

        .mobile-backdrop.show {
          opacity: 1;

          visibility: visible;
        }

        /* =========================================================
           MOBILE DRAWER
        ========================================================= */

        .mobile-menu {
          position: fixed;

          top: 0;
          right: 0;

          width:
            min(390px, 92vw);

          height: 100vh;

          z-index: 5001;

          display: flex;
          flex-direction: column;

          background:
            var(--sic-white);

          border-left:
            4px solid
            var(--sic-green);

          box-shadow:
            -15px 0 45px
            rgba(0, 0, 0, 0.22);

          transform:
            translateX(105%);

          visibility: hidden;

          transition:
            transform 0.32s
            cubic-bezier(0.4, 0, 0.2, 1),
            visibility 0.32s ease;

          overflow-y: auto;
          overflow-x: hidden;
        }

        .mobile-menu.show {
          transform:
            translateX(0);

          visibility: visible;
        }

        /* =========================================================
           MOBILE MENU HEADER
        ========================================================= */

        .mobile-menu-header {
          min-height: 91px;

          padding:
            15px 19px;

          display: flex;
          align-items: center;
          justify-content: space-between;

          background:
            var(--sic-white);

          border-bottom:
            1px solid
            var(--sic-border);
        }

        .mobile-brand {
          min-width: 0;

          display: flex;
          align-items: center;

          gap: 10px;
        }

        .mobile-brand img {
          width: 49px;
          height: 49px;

          flex-shrink: 0;

          object-fit: contain;

          background:
            var(--sic-white);

          border:
            2px solid
            var(--sic-green);

          border-radius: 50%;
        }

        .mobile-brand div {
          min-width: 0;

          display: flex;
          flex-direction: column;
        }

        .mobile-brand strong {
          color:
            var(--sic-green-deep);

          font-size: 14px;
          font-weight: 800;

          line-height: 1.2;
        }

        .mobile-brand span {
          margin-top: 3px;

          color:
            var(--sic-gray);

          font-size: 8px;
          font-weight: 700;

          text-transform: uppercase;

          letter-spacing: 1.5px;
        }

        .mobile-close-button {
          width: 40px;
          height: 40px;

          flex-shrink: 0;

          display: flex;
          align-items: center;
          justify-content: center;

          color:
            var(--sic-green);

          background:
            var(--sic-gray-light);

          border:
            1px solid
            #e0e8e1;

          border-radius: 7px;

          font-size: 24px;

          cursor: pointer;

          transition:
            color 0.25s ease,
            background 0.25s ease,
            border-color 0.25s ease;
        }

        .mobile-close-button:hover {
          color:
            var(--sic-black);

          background:
            var(--sic-yellow);

          border-color:
            var(--sic-yellow);
        }

        /* =========================================================
           DIVIDER
        ========================================================= */

        .mobile-menu-divider {
          width: 100%;
          height: 1px;

          background:
            linear-gradient(
              90deg,
              transparent,
              #e4e4e4,
              transparent
            );
        }

        /* =========================================================
           MOBILE NAVIGATION
        ========================================================= */

        .mobile-navigation {
          padding:
            18px 15px;
        }

        .mobile-navigation ul {
          margin: 0;
          padding: 0;

          display: flex;
          flex-direction: column;

          gap: 6px;

          list-style: none;
        }

        .mobile-navigation li {
          width: 100%;
        }

        .mobile-navigation li a {
          width: 100%;

          min-height: 54px;

          padding:
            0 15px;

          display: flex;
          align-items: center;
          justify-content: space-between;

          color:
            var(--sic-text);

          background:
            transparent;

          border:
            1px solid transparent;

          border-radius: 7px;

          font-size: 14px;
          font-weight: 600;

          text-decoration: none;

          transition:
            color 0.25s ease,
            background 0.25s ease,
            border-color 0.25s ease,
            transform 0.25s ease;
        }

        .mobile-navigation li a:hover {
          color:
            var(--sic-green);

          background:
            var(--sic-green-soft);

          border-color:
            rgba(8, 127, 35, 0.12);

          transform:
            translateX(3px);
        }

        .mobile-navigation li.active
          > a:not(.mobile-login-button) {

          color:
            var(--sic-green);

          background:
            rgba(8, 127, 35, 0.07);

          border-color:
            rgba(8, 127, 35, 0.16);

          font-weight: 700;
        }

        .mobile-arrow {
          color:
            var(--sic-green);

          font-size: 18px;

          transition:
            transform 0.2s ease;
        }

        .mobile-navigation li a:hover
          .mobile-arrow {

          color:
            var(--sic-yellow-dark);

          transform:
            translateX(4px);
        }

        /* =========================================================
           MOBILE LOGIN
        ========================================================= */

        .mobile-login-button {
          justify-content: center !important;

          margin-top: 8px;

          color:
            var(--sic-black) !important;

          background:
            linear-gradient(
              135deg,
              var(--sic-yellow-light),
              var(--sic-yellow)
            ) !important;

          border-color:
            var(--sic-yellow) !important;

          font-weight: 800 !important;

          box-shadow:
            0 5px 15px
            rgba(244, 196, 0, 0.22);
        }

        .mobile-login-button:hover {
          color:
            var(--sic-black) !important;

          background:
            var(--sic-yellow-light)
            !important;

          transform:
            translateY(-1px) !important;
        }

        /* =========================================================
           MOBILE CONTACT
        ========================================================= */

        .mobile-contact {
          margin-top: auto;

          padding:
            16px 20px;

          display: flex;
          flex-direction: column;

          gap: 13px;
        }

        .mobile-contact-item {
          display: flex;
          align-items: flex-start;

          gap: 11px;

          color:
            #606060;

          font-size: 11px;

          line-height: 1.55;
        }

        .mobile-contact-item svg {
          flex-shrink: 0;

          margin-top: 2px;

          color:
            var(--sic-green);
        }

        /* =========================================================
           MOBILE SOCIALS
        ========================================================= */

        .mobile-social-section {
          padding:
            17px 20px;

          display: flex;
          align-items: center;
          justify-content: space-between;

          gap: 15px;
        }

        .mobile-social-section > span {
          color:
            var(--sic-gray);

          font-size: 10px;
          font-weight: 700;

          text-transform: uppercase;

          letter-spacing: 1px;
        }

        .mobile-socials {
          display: flex;
          align-items: center;

          gap: 7px;
        }

        .mobile-socials a {
          width: 34px;
          height: 34px;

          display: flex;
          align-items: center;
          justify-content: center;

          color:
            var(--sic-green);

          background:
            #f2f7f3;

          border:
            1px solid
            #dce8df;

          border-radius: 50%;

          text-decoration: none;

          transition:
            color 0.25s ease,
            background 0.25s ease,
            border-color 0.25s ease,
            transform 0.25s ease;
        }

        .mobile-socials a:hover {
          color:
            var(--sic-black);

          background:
            var(--sic-yellow);

          border-color:
            var(--sic-yellow);

          transform:
            translateY(-2px);
        }

        /* =========================================================
           MOBILE FOOTER
        ========================================================= */

        .mobile-menu-footer {
          padding:
            18px 20px 25px;

          display: flex;
          flex-direction: column;

          text-align: center;

          background:
            #fafafa;
        }

        .mobile-menu-footer span {
          color:
            var(--sic-green-deep);

          font-size: 12px;
          font-weight: 800;
        }

        .mobile-menu-footer small {
          margin-top: 3px;

          color:
            var(--sic-yellow-dark);

          font-size: 8px;
          font-weight: 700;

          text-transform: uppercase;

          letter-spacing: 1.5px;
        }

        /* =========================================================
           PAGE SPACING
        ========================================================= */

        .navbar-spacing {
          width: 100%;
          height: 128px;
        }

        /* =========================================================
           LARGE LAPTOP
        ========================================================= */

        @media (max-width: 1200px) {

          .top-navbar-inner,
          .navbar-main-inner {
            width:
              min(100% - 30px, 1150px);
          }

          .logo-section {
            min-width: 220px;
          }

          .school-name h1 {
            font-size: 17px;
          }

          .school-name h1 .ignite-s {
            font-size: 1.08em;
            font-weight: 900;
          }

          .nav-links {
            gap: 1px;
          }

          .nav-links li > a {
            padding:
              0 10px;

            font-size: 12px;
          }
        }

        /* =========================================================
           TABLET
        ========================================================= */

        @media (max-width: 980px) {

          .top-navbar {
            display: none;
          }

          .navbar-main {
            height: 76px;
          }

          .site-header-scrolled .navbar-main {
            height: 70px;
          }

          .navbar-main-inner {
            width:
              calc(100% - 30px);
          }

          .desktop-navigation {
            display: none;
          }

          .mobile-toggle {
            display: flex;
          }

          .logo-wrapper {
            width: 53px;
            height: 53px;
          }

          .school-name h1 {
            font-size: 17px;
            font-weight: 800;
          }

          .school-name h1 .ignite-s {
            font-weight: 900;
          }

          .school-name p {
            font-size: 8px;
          }

          .navbar-spacing {
            height: 76px;
          }
        }

        /* =========================================================
           MOBILE
        ========================================================= */

        @media (max-width: 600px) {

          .navbar-main {
            height: 70px;
          }

          .site-header-scrolled .navbar-main {
            height: 66px;
          }

          .navbar-main-inner {
            width:
              calc(100% - 22px);

            gap: 10px;
          }

          .logo-section {
            min-width: 0;

            gap: 9px;
          }

          .logo-wrapper {
            width: 46px;
            height: 46px;

            border-width: 2px;
          }

          .school-name h1 {
            font-size: 14px;

            font-weight: 800;

            letter-spacing: -0.25px;
          }

          .school-name h1 .ignite-s {
            font-size: 1.08em;

            font-weight: 900;

            color:
              var(--sic-red);
          }

          .school-name p {
            margin-top: 3px;

            font-size: 7px;

            letter-spacing: 1px;
          }

          .mobile-toggle {
            width: 43px;
            height: 43px;

            font-size: 25px;
          }

          .navbar-spacing {
            height: 70px;
          }

          .mobile-menu {
            width:
              min(380px, 94vw);
          }
        }

        /* =========================================================
           SMALL PHONES
        ========================================================= */

        @media (max-width: 390px) {

          .navbar-main-inner {
            width:
              calc(100% - 18px);
          }

          .school-name h1 {
            font-size: 13px;

            font-weight: 800;
          }

          .school-name h1 .ignite-s {
            font-size: 1.08em;

            font-weight: 900;
          }

          .school-name p {
            font-size: 6.5px;
          }

          .logo-wrapper {
            width: 43px;
            height: 43px;
          }

          .mobile-toggle {
            width: 40px;
            height: 40px;
          }

          .mobile-menu-header {
            padding-left: 16px;
            padding-right: 16px;
          }

          .mobile-brand strong {
            font-size: 13px;
          }

          .mobile-navigation {
            padding-left: 12px;
            padding-right: 12px;
          }
        }

        /* =========================================================
           VERY SMALL PHONES
        ========================================================= */

        @media (max-width: 320px) {

          .school-name h1 {
            font-size: 12px;

            font-weight: 800;
          }

          .school-name h1 .ignite-s {
            font-size: 1.08em;

            font-weight: 900;
          }

          .school-name p {
            display: none;
          }

          .logo-wrapper {
            width: 40px;
            height: 40px;
          }

          .mobile-toggle {
            width: 38px;
            height: 38px;
          }
        }

        /* =========================================================
           ACCESSIBILITY
        ========================================================= */

        .nav-links a:focus-visible,
        .mobile-navigation a:focus-visible,
        .mobile-toggle:focus-visible,
        .mobile-close-button:focus-visible,
        .top-socials a:focus-visible,
        .mobile-socials a:focus-visible {

          outline:
            3px solid
            var(--sic-yellow);

          outline-offset:
            3px;
        }

        /* =========================================================
           REDUCED MOTION
        ========================================================= */

        @media (prefers-reduced-motion: reduce) {

          .site-header,
          .navbar-main,
          .logo-section,
          .top-socials a,
          .nav-links li > a,
          .nav-links .login-button,
          .mobile-toggle,
          .mobile-menu,
          .mobile-backdrop,
          .mobile-navigation li a,
          .mobile-socials a,
          .school-name h1 .ignite-s {

            transition: none !important;
          }
        }
      `}</style>

      <header
        className={`site-header ${
          scrolled
            ? "site-header-scrolled"
            : ""
        }`}
      >

        {/* =====================================================
            TOP INFORMATION BAR
        ===================================================== */}

        <div className="top-navbar">

          <div className="top-navbar-inner">

            <div className="top-navbar-left">

              <div className="top-item location-item">

                <FaMapMarkerAlt />

                <span>
                  Johnsonville, Monrovia, Liberia
                  Monrovia
                  &nbsp; • &nbsp;
                  +231-776-4940-10
                  &nbsp; / &nbsp;
                  +231-880-5858-55
                </span>

              </div>

            </div>

            <div className="top-navbar-right">

              <span className="follow-text">
                Follow Us
              </span>

              <div className="top-socials">

                <a
                  href="https://facebook.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                >
                  <FaFacebookF />
                </a>

                <a
                  href="https://youtube.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="YouTube"
                >
                  <FaYoutube />
                </a>

                <a
                  href="mailto:info@messiahmission.edu"
                  aria-label="Email"
                >
                  <FaEnvelope />
                </a>

              </div>

            </div>

          </div>

        </div>

        {/* =====================================================
            MAIN WHITE NAVBAR
        ===================================================== */}

        <div className="navbar-main">

          <div className="navbar-main-inner">

            <Link
              to="/"
              className="logo-section"
              aria-label="Home"
            >

              <div className="logo-wrapper">

                <img
                  src={logo}
                  alt="Ignite S Vocational School"
                  className="navbar-logo"
                />

              </div>

              <div className="school-name">

                <h1>
                  Ignite<span className="ignite-s">S</span>
                </h1>

                <p>
                  Skill Center
                </p>

              </div>

            </Link>

            {/* =================================================
                DESKTOP NAVIGATION
            ================================================= */}

            <nav
              className="desktop-navigation"
              aria-label="Main navigation"
            >

              <ul className="nav-links">

                {navLinks.map((link) => (

                  <li
                    key={link.path}
                    className={
                      isActive(link.path)
                        ? "active"
                        : ""
                    }
                  >

                    <Link
                      to={link.path}
                      className={
                        link.button
                          ? "login-button"
                          : ""
                      }
                    >
                      {link.text}
                    </Link>

                  </li>

                ))}

              </ul>

            </nav>

            {/* =================================================
                MOBILE MENU TOGGLE
            ================================================= */}

            <button
              ref={mobileToggleRef}
              type="button"
              className={`mobile-toggle ${
                menuOpen
                  ? "toggle-active"
                  : ""
              }`}
              onClick={toggleMenu}
              aria-label={
                menuOpen
                  ? "Close navigation menu"
                  : "Open navigation menu"
              }
              aria-expanded={menuOpen}
              aria-controls="mobile-navigation"
            >

              {menuOpen ? (
                <IoClose />
              ) : (
                <HiOutlineMenuAlt3 />
              )}

            </button>

          </div>

        </div>

        {/* =====================================================
            MOBILE BACKDROP
        ===================================================== */}

        <div
          className={`mobile-backdrop ${
            menuOpen
              ? "show"
              : ""
          }`}
          onClick={closeMenu}
          aria-hidden="true"
        />

        {/* =====================================================
            MOBILE DRAWER
        ===================================================== */}

        <aside
          ref={mobileMenuRef}
          id="mobile-navigation"
          className={`mobile-menu ${
            menuOpen
              ? "show"
              : ""
          }`}
          aria-hidden={!menuOpen}
        >

          <div className="mobile-menu-header">

            <div className="mobile-brand">

              <img
                src={logo}
                alt="Ignite S Vocational School"
              />

              <div>

                <strong>
                  Ignite <span
                    style={{
                      color: "#d90429",
                      fontWeight: 900,
                      fontFamily:
                        '"Montserrat", "Poppins", "Segoe UI", Arial, sans-serif',
                    }}
                  >
                    S
                  </span>
                </strong>

                <span>
                  Vocational School
                </span>

              </div>

            </div>

            <button
              type="button"
              className="mobile-close-button"
              onClick={closeMenu}
              aria-label="Close navigation menu"
            >
              <IoClose />
            </button>

          </div>

          <div className="mobile-menu-divider" />

          {/* MOBILE NAVIGATION */}

          <nav
            className="mobile-navigation"
            aria-label="Mobile navigation"
          >

            <ul>

              {navLinks.map((link) => (

                <li
                  key={link.path}
                  className={
                    isActive(link.path)
                      ? "active"
                      : ""
                  }
                >

                  <Link
                    to={link.path}
                    onClick={closeMenu}
                    tabIndex={
                      menuOpen
                        ? 0
                        : -1
                    }
                    className={
                      link.button
                        ? "mobile-login-button"
                        : ""
                    }
                  >

                    <span>
                      {link.text}
                    </span>

                    {!link.button && (
                      <span className="mobile-arrow">
                        →
                      </span>
                    )}

                  </Link>

                </li>

              ))}

            </ul>

          </nav>

          {/* MOBILE CONTACT */}

          <div className="mobile-contact">

            <div className="mobile-contact-item">

              <FaMapMarkerAlt />

              <span>
                    Johnsonville, Monrovia, Liberia
                <br />
                +231-776-4940-10 /
                +231-880-5858-55
              </span>

            </div>

            <div className="mobile-contact-item">

              <FaPhoneAlt />

              <span>
                Contact the School
                Administration
              </span>

            </div>

          </div>

          <div className="mobile-menu-divider" />

          {/* MOBILE SOCIALS */}

          <div className="mobile-social-section">

            <span>
              Connect With Us
            </span>

            <div className="mobile-socials">

              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <FaFacebookF />
              </a>

              <a
                href="https://youtube.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
              >
                <FaYoutube />
              </a>

              <a
                href="mailto:info@messiahmission.edu"
                aria-label="Email"
              >
                <FaEnvelope />
              </a>

            </div>

          </div>

          {/* MOBILE FOOTER */}

          <div className="mobile-menu-footer">

            <span>
              Ignite <span
                style={{
                  color: "#d90429",
                  fontWeight: 900,
                }}
              >
                S
              </span>
            </span>

            <small>
              Vocational School
            </small>

          </div>

        </aside>

      </header>

      <div className="navbar-spacing" />
    </>
  );
};

export default Navbar;